/* A list that lives inside another record, edited one row at a time.
 *
 * Subnets, SSIDs and tunnels are JSON on the depot's network row rather than
 * lists of their own — §6.1, because they are nested and never queried
 * independently. That decision is right for the data and it used to shape the
 * editing badly: with no row of its own there was no drawer to open, so the
 * whole list was edited at once as a grid of bare input boxes with the labels
 * inside them as placeholders.
 *
 * Which worked at four fields and stopped working at nine. A placeholder
 * disappears the moment you type, so on a half-filled subnet you could not tell
 * the gateway box from the DHCP server box without deleting something to find
 * out. Every other register in the app edits one record in a labelled drawer;
 * this was the one place that did not, and it had become the worst screen to
 * add to.
 *
 * So a row opens in the same drawer, drawn by the same renderer, from the same
 * field definitions — which means hints, sections, tick boxes and selects all
 * arrive here for free, and a fix to a date input is a fix here too.
 *
 * The write is unchanged: the whole array goes back in one save, because it is
 * one column. Nothing is written until Save, so cancelling costs nothing.
 */

import { useState } from 'react';
import { Button, DataTable, Drawer, EmptyState, Panel, type Column } from '../ui';
import { FieldInput, missingRequired, type RenderableField } from './RecordField';
import s from './screens.module.scss';

/** Being added, being edited, or neither. Held as one value because "which row
    is open" and "is this a new one" are the same question. */
type Editing<T> = { index: number; draft: T } | null;

export function NestedList<T extends Record<string, unknown>>({
  title,
  sub,
  label,
  icon,
  rows,
  blank,
  columns,
  fields,
  rowKey,
  initialSort,
  emptyTitle,
  onSave,
  saving = false,
  disabled = false,
}: {
  title: string;
  sub?: string;
  /** Singular, lower case — "subnet", "SSID". Used in every prompt. */
  label: string;
  icon: string;
  rows: T[];
  /** A new, empty row. Written out by the caller so every key exists. */
  blank: T;
  columns: Column<T>[];
  fields: RenderableField[];
  rowKey: (row: T) => string;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  emptyTitle: string;
  onSave: (rows: T[]) => void;
  saving?: boolean;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState<Editing<T>>(null);

  /* Index -1 means a row that does not exist yet. Kept as a number rather than a
     separate flag so save and delete need one path, not two. */
  const isNew = editing?.index === -1;

  const commit = (next: T[]) => {
    onSave(next);
    setEditing(null);
  };

  const save = () => {
    if (!editing) return;
    commit(
      isNew
        ? [...rows, editing.draft]
        : rows.map((row, i) => (i === editing.index ? editing.draft : row)),
    );
  };

  const remove = () => {
    if (!editing || isNew) return;
    const what = String(editing.draft[fields[0]?.id] ?? `this ${label}`);
    if (!window.confirm(`Remove ${what}? The change is saved immediately.`)) return;
    commit(rows.filter((_, i) => i !== editing.index));
  };

  const missing = editing ? missingRequired(fields, editing.draft) : [];

  return (
    <>
      <Panel
        title={title}
        sub={sub}
        flush
        actions={
          <Button
            icon="ti-plus"
            onClick={() => setEditing({ index: -1, draft: { ...blank } })}
            disabled={disabled}
          >
            Add {label}
          </Button>
        }
      >
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={rowKey}
          initialSort={initialSort}
          /* Inside a drawer, with more below it: let the drawer scroll, as it did. */
          fill={false}
          /* The row is the way in, as it is on every other register. The old
             editor hid all of this behind one Edit button in the header. */
          onRowClick={
            disabled
              ? undefined
              : (row) => setEditing({ index: rows.indexOf(row), draft: { ...row } })
          }
          empty={<EmptyState icon={icon} title={emptyTitle} />}
        />
      </Panel>

      <Drawer
        open={editing !== null}
        title={isNew ? `New ${label}` : `Edit ${label}`}
        sub={editing && !isNew ? String(editing.draft[fields[0]?.id] ?? '') : undefined}
        avatar={<i className={`ti ${icon}`} aria-hidden />}
        onClose={() => setEditing(null)}
        footer={
          <div className={s.actions}>
            <Button
              primary
              icon={saving ? 'ti-loader-2' : 'ti-device-floppy'}
              spinning={saving}
              disabled={saving || missing.length > 0}
              onClick={save}
            >
              {saving ? 'Saving…' : isNew ? `Add ${label}` : 'Save changes'}
            </Button>
            {!isNew && (
              <Button icon="ti-trash" disabled={saving} onClick={remove}>
                Remove
              </Button>
            )}
          </div>
        }
      >
        {editing &&
          fields.map((field, i) => (
            <div key={field.id}>
              {field.section && field.section !== fields[i - 1]?.section && (
                <div className={s.sectionLabel}>{field.section}</div>
              )}
              <FieldInput
                field={field}
                value={editing.draft[field.id]}
                onChange={(v) =>
                  setEditing((prev) =>
                    prev ? { ...prev, draft: { ...prev.draft, [field.id]: v } } : prev,
                  )
                }
                lookups={{ people: [], suppliers: [] }}
                values={editing.draft}
              />
            </div>
          ))}

        {missing.length > 0 && (
          <div className={s.ownedNote}>
            <i className="ti ti-alert-triangle" aria-hidden />
            <span>
              {missing.map((f) => f.label).join(' and ')} {missing.length === 1 ? 'is' : 'are'}{' '}
              required.
            </span>
          </div>
        )}
      </Drawer>
    </>
  );
}
